import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { useAsync } from '../../hooks/useAsync';
import { useQueryParams } from '../../hooks/useQueryParams';
import { dateOnly, eur, usd } from '../../services/format';
import { Alert, Badge, Button, Cell, Field, Filters, Input, PageTitle, Pager, Row, Select, Table, statusTone } from '../../components/ui';
import type { SubscriptionBrief } from '../../types/api';

const PAGE_SIZE = 50;

/** "Medium v2 × 5" — what an organization is on. */
export function subscriptionLabel(sub: SubscriptionBrief | null, t: (k: string) => string): string {
  if (!sub) return t('billing.none');
  return `${sub.offer_name} v${sub.version} × ${sub.seats}`;
}

export function subscriptionTone(status: string) {
  return status === 'active' ? 'good' : status === 'trialing' ? 'accent' : status === 'past_due' ? 'bad' : 'neutral';
}

export function OrgsList() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { get, page, set, reset, setPage } = useQueryParams();
  const q = get('q');
  const kind = get('kind');
  const status = get('status');
  const offer = get('offer');
  const offers = useAsync(() => api.offers(), []);
  const { data, error, loading } = useAsync(() => api.organizations({ q: q || undefined, kind: kind || undefined, status: status || undefined, offer: offer || undefined, page, page_size: PAGE_SIZE }), [q, kind, status, offer, page]);

  return (
    <div>
      <PageTitle title={t('orgs.title')} subtitle={data ? t('common.count', { count: data.total }) : undefined} actions={<Button variant="primary" onClick={() => navigate('/organizations/new')}>{t('orgs.new')}</Button>} />
      <Filters onReset={reset}>
        <Field label={t('common.search')} className="w-64">
          <Input value={q} onChange={(e) => set({ q: e.target.value })} placeholder={t('orgs.searchPlaceholder')} />
        </Field>
        <Field label={t('orgs.kindLabel')}>
          <Select value={kind} onChange={(e) => set({ kind: e.target.value })}>
            <option value="">{t('common.any')}</option>
            <option value="team">{t('orgs.kind.team')}</option>
            <option value="personal">{t('orgs.kind.personal')}</option>
          </Select>
        </Field>
        <Field label={t('orgs.statusLabel')}>
          <Select value={status} onChange={(e) => set({ status: e.target.value })}>
            <option value="">{t('common.any')}</option>
            <option value="active">{t('orgs.status.active')}</option>
            <option value="suspended">{t('orgs.status.suspended')}</option>
            <option value="archived">{t('orgs.status.archived')}</option>
          </Select>
        </Field>
        <Field label={t('billing.offer')}>
          <Select value={offer} onChange={(e) => set({ offer: e.target.value })} data-testid="filter-offer">
            <option value="">{t('common.any')}</option>
            {(offers.data ?? []).map((o) => (
              <option key={o.key} value={o.key}>
                {o.name}
              </option>
            ))}
          </Select>
        </Field>
      </Filters>
      {error && <Alert>{error}</Alert>}
      <Table head={[t('orgs.name'), t('orgs.kindLabel'), t('orgs.statusLabel'), t('billing.subscription'), t('billing.periodEnd'), t('orgs.members'), t('orgs.spend'), t('orgs.cost')]} empty={loading ? t('common.loading') : t('common.empty')}>
        {(data?.items ?? []).map((o) => (
          <Row key={o.id} onClick={() => navigate(`/organizations/${o.id}`)}>
            <Cell>
              {o.name} <span className="font-mono text-[10.5px] text-faint">#{o.id}</span>
            </Cell>
            <Cell>{t(`orgs.kind.${o.kind}`)}</Cell>
            <Cell>
              <Badge tone={statusTone(o.status)}>{t(`orgs.status.${o.status}`)}</Badge>
            </Cell>
            <Cell mono>
              {subscriptionLabel(o.subscription, t)}
              {o.subscription && (
                <>
                  {' '}
                  <Badge tone={subscriptionTone(o.subscription.status)}>{t(`billing.status.${o.subscription.status}`)}</Badge>
                </>
              )}
            </Cell>
            <Cell mono>{o.subscription ? dateOnly(o.subscription.current_period_end, i18n.language) : '—'}</Cell>
            <Cell mono>{o.member_count}</Cell>
            <Cell mono>{eur(o.billed_eur, i18n.language)}</Cell>
            <Cell mono>{usd(o.cost_usd, i18n.language)}</Cell>
          </Row>
        ))}
      </Table>
      {data && <Pager page={data.page} pageSize={data.page_size} total={data.total} onPage={setPage} />}
    </div>
  );
}
